import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { WorkspaceCanvasView, Canvas } from "@/types/models";
import type { RootState } from "@store/index";
import {
  addCanvas,
  renameCanvas as renameCanvasReducer,
  removeCanvas as removeCanvasReducer,
} from "@store/slices/workspaceSlice";
import { setBaseView } from "@store/slices/viewSlice";
import { selectCanvas } from "@store/thunks/workspaceThunks";
import {
  createCanvas as createCanvasApi,
  renameCanvas as renameCanvasApi,
  deleteCanvas as deleteCanvasApi,
  getWorkspaceCanvasesManage,
} from "@services/api";

interface WorkspaceCanvasesState {
  allWorkspaceCanvases: WorkspaceCanvasView[];
  loading: boolean;
  error: string | null;
}

const initialState: WorkspaceCanvasesState = {
  allWorkspaceCanvases: [],
  loading: false,
  error: null,
};

export const loadAllWorkspaceCanvases = createAsyncThunk<
  WorkspaceCanvasView[],
  string,
  { rejectValue: string }
>("workspaceCanvases/loadAll", async (workspaceId, { rejectWithValue }) => {
  const result = await getWorkspaceCanvasesManage(workspaceId);
  if (!result.success) {
    return rejectWithValue(result.error.message || "Failed to load canvases");
  }
  return result.data;
});

// Create: API → push to manage slice via fulfilled + dispatch addCanvas to
// per-user slice + select the new canvas + flip to canvas view.
export const createCanvas = createAsyncThunk<
  Canvas,
  { workspaceId: string; canvasName: string },
  { state: RootState; rejectValue: string }
>(
  "workspaceCanvases/create",
  async ({ workspaceId, canvasName }, { dispatch, rejectWithValue }) => {
    const result = await createCanvasApi(workspaceId, {
      canvas_name: canvasName,
    });
    if (!result.success) {
      return rejectWithValue(result.error.message || "Failed to create canvas");
    }
    dispatch(addCanvas(result.data));
    dispatch(setBaseView("canvas"));
    dispatch(selectCanvas(result.data.canvas_id));
    return result.data;
  }
);

// Rename: API → update manage slice via fulfilled + dispatch renameCanvas to
// per-user slice so the dropdown stays current.
export const renameCanvas = createAsyncThunk<
  { canvasId: string; canvasName: string },
  { canvasId: string; canvasName: string },
  { state: RootState; rejectValue: string }
>(
  "workspaceCanvases/rename",
  async ({ canvasId, canvasName }, { getState, dispatch, rejectWithValue }) => {
    const workspaceId = getState().workspace.workspace_id;
    if (!workspaceId) return rejectWithValue("No active workspace");
    const result = await renameCanvasApi(workspaceId, canvasId, canvasName);
    if (!result.success) {
      return rejectWithValue(result.error.message || "Failed to rename canvas");
    }
    dispatch(renameCanvasReducer({ canvasId, canvasName }));
    return { canvasId, canvasName };
  }
);

// Delete: API → filter from manage via fulfilled + dispatch removeCanvas to
// per-user slice so the reader cascade fires.
export const deleteCanvas = createAsyncThunk<
  string,
  string,
  { state: RootState; rejectValue: string }
>(
  "workspaceCanvases/delete",
  async (canvasId, { getState, dispatch, rejectWithValue }) => {
    const workspaceId = getState().workspace.workspace_id;
    if (!workspaceId) return rejectWithValue("No active workspace");
    const result = await deleteCanvasApi(workspaceId, canvasId);
    if (!result.success) {
      return rejectWithValue(result.error.message || "Failed to delete canvas");
    }
    dispatch(removeCanvasReducer(canvasId));
    return canvasId;
  }
);

const workspaceCanvasesSlice = createSlice({
  name: "workspaceCanvases",
  initialState,
  reducers: {
    clearWorkspaceCanvases: (state) => {
      state.allWorkspaceCanvases = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAllWorkspaceCanvases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        loadAllWorkspaceCanvases.fulfilled,
        (state, action: PayloadAction<WorkspaceCanvasView[]>) => {
          state.loading = false;
          state.allWorkspaceCanvases = action.payload;
        }
      )
      .addCase(loadAllWorkspaceCanvases.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to load workspace canvases";
      })
      .addCase(createCanvas.fulfilled, (state, action) => {
        const c = action.payload;
        // The API returns Canvas (per-user shape). For the manage list we
        // synthesize a minimal WorkspaceCanvasView entry — aggregate counts
        // start at zero for a brand new canvas.
        const view: WorkspaceCanvasView = {
          canvas_id: c.canvas_id,
          workspace_id: c.workspace_id,
          canvas_name: c.canvas_name,
          canvas_data: c.canvas_data,
          description: c.description,
          instructions: c.instructions,
          due_date: c.due_date,
          required: c.required ?? false,
          assignment_kind: (c as { assignment_kind?: string }).assignment_kind ?? "optional",
          assigned_by_user_id: c.assigned_by_user_id,
          assigned_by_username: null,
          assigned_user_count: 0,
          completed_count: 0,
          in_progress_count: 0,
          not_started_count: 0,
          my_status: null,
          my_points: null,
          my_max_points: null,
          my_submitted_at: null,
          show_collaborator_status: c.show_collaborator_status ?? false,
          created_at: c.created_at,
        };
        if (
          !state.allWorkspaceCanvases.some(
            (existing) => existing.canvas_id === view.canvas_id
          )
        ) {
          state.allWorkspaceCanvases.unshift(view);
        }
      })
      .addCase(renameCanvas.fulfilled, (state, action) => {
        const { canvasId, canvasName } = action.payload;
        const entry = state.allWorkspaceCanvases.find(
          (c) => c.canvas_id === canvasId
        );
        if (entry) {
          entry.canvas_name = canvasName;
        }
      })
      .addCase(deleteCanvas.fulfilled, (state, action) => {
        state.allWorkspaceCanvases = state.allWorkspaceCanvases.filter(
          (c) => c.canvas_id !== action.payload
        );
      });
  },
});

export const { clearWorkspaceCanvases } = workspaceCanvasesSlice.actions;

export const selectAllWorkspaceCanvases = (state: RootState) =>
  state.workspaceCanvases.allWorkspaceCanvases;
export const selectWorkspaceCanvasesLoading = (state: RootState) =>
  state.workspaceCanvases.loading;
export const selectWorkspaceCanvasesError = (state: RootState) =>
  state.workspaceCanvases.error;

export default workspaceCanvasesSlice.reducer;
